import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Download,
  Copy,
  Check,
  RefreshCw,
  Search,
  HardDrive,
  Cloud,
  Layers,
  Image as ImageIcon,
  ExternalLink,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { TransparentAsset, Product } from '../types';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

interface TransparentImageLibraryProps {
  onSelectForShowcase?: (asset: TransparentAsset) => void;
  availableProducts?: Product[];
  initialSelectedProductCode?: string;
}

export const TransparentImageLibrary: React.FC<TransparentImageLibraryProps> = ({
  onSelectForShowcase,
  availableProducts = [],
  initialSelectedProductCode = ''
}) => {
  const [assets, setAssets] = useState<TransparentAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [syncingCloud, setSyncingCloud] = useState<string | null>(null);
  const [processingNew, setProcessingNew] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedProductCode, setSelectedProductCode] = useState(initialSelectedProductCode);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (initialSelectedProductCode) {
      setSelectedProductCode(initialSelectedProductCode);
    }
  }, [initialSelectedProductCode]);

  // Fetch all saved transparent assets
  const fetchAssets = async () => {
    try {
      setLoading(true);
      // 1. Fetch server-cached local files
      const res = await fetch('/api/transparent-assets');
      let localAssets: TransparentAsset[] = [];
      if (res.ok) {
        const data = await res.json();
        localAssets = data.assets || [];
      }

      // 2. Fetch Firestore registered cloud assets
      try {
        const cloudSnap = await getDocs(collection(db, 'transparent_assets'));
        const cloudAssets: TransparentAsset[] = [];
        cloudSnap.forEach((docSnap) => {
          cloudAssets.push({ id: docSnap.id, ...docSnap.data() } as TransparentAsset);
        });

        // Merge keeping unique
        const merged = [...localAssets];
        cloudAssets.forEach((c) => {
          const exists = merged.find((m) => m.transparentUrl === c.transparentUrl || m.id === c.id);
          if (exists) {
            exists.cloudStorageUrl = c.cloudStorageUrl || exists.cloudStorageUrl;
          } else if (c.cloudStorageUrl) {
            merged.push(c);
          }
        });
        setAssets(merged);
      } catch (fbErr) {
        console.warn('Firestore fetch note:', fbErr);
        setAssets(localAssets);
      }
    } catch (err: any) {
      console.error('Failed to load transparent assets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  // Copy Image URL to clipboard
  const handleCopyUrl = (asset: TransparentAsset) => {
    const fullUrl = asset.cloudStorageUrl || `${window.location.origin}${asset.transparentUrl}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(fullUrl).catch(() => {});
      }
    } catch (e) {
      console.warn('Clipboard write error:', e);
    }
    setCopiedId(asset.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Upload/Sync specific asset to Firebase Cloud Storage & Firestore
  const handleSyncToCloud = async (asset: TransparentAsset) => {
    try {
      setSyncingCloud(asset.id);
      setStatusMsg(null);

      // Fetch the transparent image blob
      const imgRes = await fetch(asset.transparentUrl);
      if (!imgRes.ok) throw new Error('ไม่สามารถดึงข้อมูลไฟล์ภาพโปร่งแสงได้');
      const blob = await imgRes.blob();

      // Convert to base64 data URL for upload
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        try {
          const base64data = reader.result as string;
          const filename = asset.transparentUrl.split('/').pop() || `transparent-${asset.id}.png`;
          const storageRef = ref(storage, `transparent_products/${filename}`);

          await uploadString(storageRef, base64data, 'data_url');
          const downloadUrl = await getDownloadURL(storageRef);

          // Save reference to Firestore
          const assetRef = doc(db, 'transparent_assets', asset.id);
          const updatedAsset = {
            ...asset,
            cloudStorageUrl: downloadUrl,
            updatedAt: new Date().toISOString()
          };
          await setDoc(assetRef, updatedAsset, { merge: true });

          setAssets((prev) =>
            prev.map((item) => (item.id === asset.id ? { ...item, cloudStorageUrl: downloadUrl } : item))
          );

          setStatusMsg({
            type: 'success',
            text: `ซิงค์ภาพ "${asset.productName}" ขึ้น Firebase Cloud Storage เรียบร้อยแล้ว!`,
          });
        } catch (subErr: any) {
          console.error('Storage upload error:', subErr);
          setStatusMsg({ type: 'error', text: 'เกิดข้อผิดพลาดในการอัปโหลดขึ้น Cloud Storage' });
        } finally {
          setSyncingCloud(null);
        }
      };
    } catch (err: any) {
      console.error('Sync error:', err);
      setStatusMsg({ type: 'error', text: 'เกิดข้อผิดพลาด: ' + (err.message || 'ล้มเหลว') });
      setSyncingCloud(null);
    }
  };

  // Process a new product image to transparent PNG using Photoroom API directly
  const handleProcessProduct = async () => {
    const code = selectedProductCode.trim();
    if (!code) return;

    // Find product in local list or fallback
    const targetProduct = availableProducts.find(
      (p) => p.code === code || p.code.toLowerCase() === code.toLowerCase()
    );

    const imageUrl = targetProduct?.image || `https://www.giffarine.com/images/product/${code}.jpg`;
    const productName = targetProduct?.name || `สินค้ากิฟฟารีน (รหัส ${code})`;

    try {
      setProcessingNew(true);
      setStatusMsg(null);

      const res = await fetch('/api/transparent-assets/process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          productCode: code,
          productName,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'ประมวลผลตัดพื้นหลังผ่าน Photoroom API ไม่สำเร็จ');
      }

      // Auto register to Firestore and update UI state instantly
      if (data.asset) {
        setAssets((prev) => [
          data.asset,
          ...prev.filter((a) => a.id !== data.asset.id && a.transparentUrl !== data.asset.transparentUrl),
        ]);

        try {
          const assetRef = doc(db, 'transparent_assets', data.asset.id);
          await setDoc(assetRef, {
            ...data.asset,
            updatedAt: new Date().toISOString()
          }, { merge: true });
        } catch (fErr) {
          console.warn('Firestore auto-register note:', fErr);
        }
      }

      setStatusMsg({
        type: 'success',
        text: `ภาพต้นฉบับคงเดิม! เชื่อมต่อ Photoroom API ดึงภาพรหัส "${code}" ตัดพื้นหลังส่งเข้า "คลังภาพสินค้าโปร่งแสง" เรียบร้อยแล้ว`,
      });

      // Re-fetch assets list to ensure sync
      await fetchAssets();
      setSelectedProductCode('');
    } catch (err: any) {
      console.error('Process error:', err);
      setStatusMsg({
        type: 'error',
        text: err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ Photoroom API เพื่อตัดพื้นหลัง',
      });
    } finally {
      setProcessingNew(false);
    }
  };

  // Permanently delete a transparent image asset from server and Firestore
  const handleDeleteAsset = async (asset: TransparentAsset) => {
    const confirmDelete = window.confirm(
      `คุณต้องการลบภาพโปร่งแสง "${asset.productName}" ออกจากคลังอย่างถาวรใช่หรือไม่?`
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(asset.id);
      setStatusMsg(null);

      // 1. Delete document from Firestore if it exists
      try {
        await deleteDoc(doc(db, 'transparent_assets', asset.id));
      } catch (fsErr) {
        console.warn('Firestore delete note:', fsErr);
      }

      // 2. Delete file from local server disk
      try {
        await fetch(`/api/transparent-assets/${encodeURIComponent(asset.id)}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transparentUrl: asset.transparentUrl }),
        });
      } catch (srvErr) {
        console.warn('Server file delete note:', srvErr);
      }

      // 3. Update UI state
      setAssets((prev) => prev.filter((item) => item.id !== asset.id && item.transparentUrl !== asset.transparentUrl));

      setStatusMsg({
        type: 'success',
        text: `ลบภาพโปร่งแสง "${asset.productName}" ออกจากคลังถาวรเรียบร้อยแล้ว`,
      });
    } catch (err: any) {
      console.error('Delete error:', err);
      setStatusMsg({
        type: 'error',
        text: 'เกิดข้อผิดพลาดในการลบภาพ: ' + (err.message || 'ล้มเหลว'),
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Filtered assets
  const filteredAssets = assets.filter(
    (a) =>
      a.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.productCode && a.productCode.includes(searchQuery))
  );

  return (
    <div className="space-y-6">
      {/* Top Banner Notice */}
      <div className="bg-gradient-to-r from-amber-500/10 via-pink-500/10 to-purple-500/10 border border-amber-200/60 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center font-bold shadow-xs shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-extrabold text-slate-900 flex items-center gap-2">
              <span>คลังภาพสินค้าโปร่งแสง (Transparent Asset Storage)</span>
              <span className="text-[11px] font-black px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                ประหยัดเครดิต 100%
              </span>
            </h3>
            <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
              ภาพทุกภาพที่ตัดพื้นหลังสำเร็จจะถูกบันทึกสำเนาลงในคลังและ Cloud Storage สามารถนำไปใช้ในแท่นโชว์ สไลด์แบนเนอร์ หรือฟีเจอร์อื่นๆ ได้ทันทีโดยไม่ต้องเสียเครดิตซ้ำ
            </p>
          </div>
        </div>

        <button
          onClick={fetchAssets}
          disabled={loading}
          className="px-3.5 py-2 bg-white text-slate-700 hover:text-pink-600 border border-slate-200 hover:border-pink-200 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-2xs shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-pink-600' : ''}`} />
          <span>รีเฟรชคลังภาพ</span>
        </button>
      </div>

      {/* Notifications */}
      {statusMsg && (
        <div
          className={`p-3.5 rounded-xl border flex items-center gap-2.5 text-xs font-bold animate-fadeIn ${
            statusMsg.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {statusMsg.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          )}
          <span>{statusMsg.text}</span>
        </div>
      )}

      {/* Action Bar: Search & New Product Process Picker */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="ค้นหาชื่อสินค้า หรือรหัสสินค้าในคลัง..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
          />
        </div>

        {/* Add New Product to Transparent Storage via Photoroom API */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              list="product-code-suggestions"
              placeholder="คีย์รหัสสินค้า (เช่น 84054)..."
              value={selectedProductCode}
              onChange={(e) => setSelectedProductCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && selectedProductCode && !processingNew) {
                  handleProcessProduct();
                }
              }}
              className="bg-slate-50 border border-slate-200 text-slate-800 text-xs rounded-xl px-3 py-2 font-bold focus:outline-none focus:ring-2 focus:ring-pink-500/20 w-48 sm:w-60"
            />
            <datalist id="product-code-suggestions">
              {availableProducts.slice(0, 100).map((p) => (
                <option key={p.code} value={p.code}>
                  [{p.code}] {p.name}
                </option>
              ))}
            </datalist>
          </div>

          <button
            onClick={handleProcessProduct}
            disabled={!selectedProductCode.trim() || processingNew}
            className="px-3.5 py-2 bg-[#BE185D] hover:bg-pink-800 text-white rounded-xl text-xs font-extrabold flex items-center gap-1.5 transition-all shadow-xs disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            title="ดึงภาพสินค้าและตัดพื้นหลังด้วย Photoroom API ทันที"
          >
            {processingNew ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>กำลังตัดด้วย Photoroom...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                <span>ดึงภาพและลบพื้นหลัง</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Grid of Saved Transparent Assets */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 text-xs flex flex-col items-center gap-2">
          <RefreshCw className="w-6 h-6 animate-spin text-pink-500" />
          <span>กำลังโหลดรายการภาพโปร่งแสงในคลัง...</span>
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-2xl border border-dashed border-slate-200 p-8">
          <Layers className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-slate-700">ไม่พบภาพโปร่งแสงที่ตรงกับเงื่อนไขค้นหา</p>
          <p className="text-xs text-slate-400 mt-1">
            เลือกสินค้าจากเมนูด้านบนเพื่อตัดพื้นหลังและบันทึกสำเนาลงในคลังถาวรได้ทันที
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredAssets.map((asset) => {
            const isCopied = copiedId === asset.id;
            const isSyncing = syncingCloud === asset.id;
            const hasCloudUrl = !!asset.cloudStorageUrl;

            return (
              <div
                key={asset.id}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-2xs hover:shadow-md transition-all flex flex-col group"
              >
                {/* Checkerboard Transparent Preview Canvas */}
                <div 
                  className="relative aspect-square w-full flex items-center justify-center p-4 border-b border-slate-100"
                  style={{
                    backgroundImage: `linear-gradient(45deg, #f1f5f9 25%, transparent 25%), linear-gradient(-45deg, #f1f5f9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f1f5f9 75%), linear-gradient(-45deg, transparent 75%, #f1f5f9 75%)`,
                    backgroundSize: `16px 16px`,
                    backgroundPosition: `0 0, 0 8px, 8px -8px, -8px 0px`,
                    backgroundColor: '#ffffff'
                  }}
                >
                  <img
                    src={asset.cloudStorageUrl || asset.transparentUrl}
                    alt={asset.productName}
                    className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />

                  {/* Badges */}
                  <div className="absolute top-2.5 left-2.5 flex flex-col gap-1">
                    {asset.productCode && (
                      <span className="px-2 py-0.5 rounded-md bg-slate-900/80 backdrop-blur-xs text-white text-[10px] font-black">
                        #{asset.productCode}
                      </span>
                    )}
                    {asset.source === 'photoroom-ai' ? (
                      <span className="px-2 py-0.5 rounded-md bg-pink-600/90 backdrop-blur-xs text-white text-[9.5px] font-extrabold flex items-center gap-1 shadow-xs">
                        <Sparkles className="w-2.5 h-2.5" />
                        Photoroom AI
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md bg-slate-800/80 backdrop-blur-xs text-white text-[9.5px] font-extrabold flex items-center gap-1 shadow-xs">
                        <Sparkles className="w-2.5 h-2.5" />
                        Studio Transparent
                      </span>
                    )}
                  </div>

                  {/* Cloud Indicator Badge & Quick Delete Button */}
                  <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
                    {hasCloudUrl ? (
                      <span
                        className="p-1 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-xs"
                        title="บันทึกบน Cloud Storage แล้ว"
                      >
                        <Cloud className="w-3 h-3" />
                      </span>
                    ) : (
                      <span
                        className="p-1 rounded-full bg-slate-400 text-white flex items-center justify-center shadow-xs"
                        title="บันทึกบน Local Server"
                      >
                        <HardDrive className="w-3 h-3" />
                      </span>
                    )}

                    <button
                      onClick={() => handleDeleteAsset(asset)}
                      disabled={deletingId === asset.id}
                      className="p-1 rounded-full bg-rose-600/90 hover:bg-rose-700 backdrop-blur-xs text-white flex items-center justify-center shadow-xs transition-all active:scale-90"
                      title="ลบภาพโปร่งแสงนี้ถาวร"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Info & Actions */}
                <div className="p-3.5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h4 className="text-xs font-black text-slate-800 line-clamp-2 leading-snug">
                      {asset.productName}
                    </h4>
                    <p className="text-[10px] text-slate-400 font-medium mt-1">
                      ขนาดไฟล์: {asset.sizeBytes ? `${Math.round(asset.sizeBytes / 1024)} KB` : 'N/A'}
                    </p>
                  </div>

                  <div className="space-y-1.5 pt-1 border-t border-slate-100">
                    {/* Action 1: Copy Link */}
                    <button
                      onClick={() => handleCopyUrl(asset)}
                      className={`w-full py-1.5 px-2.5 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 transition-all ${
                        isCopied
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }`}
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-3 h-3 text-emerald-600" />
                          <span>คัดลอกลิงก์สำเร็จ!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>คัดลอก URL ภาพโปร่งแสง</span>
                        </>
                      )}
                    </button>

                    {/* Action 2: Cloud Storage Sync */}
                    {!hasCloudUrl && (
                      <button
                        onClick={() => handleSyncToCloud(asset)}
                        disabled={isSyncing}
                        className="w-full py-1.5 px-2.5 rounded-xl text-[11px] font-bold bg-pink-50 hover:bg-pink-100 text-pink-700 border border-pink-200 flex items-center justify-center gap-1.5 transition-all"
                      >
                        <Cloud className={`w-3 h-3 ${isSyncing ? 'animate-bounce' : ''}`} />
                        <span>{isSyncing ? 'กำลังบันทึกขึ้นคลาวด์...' : 'สำรองขึ้น Cloud Storage'}</span>
                      </button>
                    )}

                    {/* Action 3: Download Direct */}
                    <a
                      href={asset.cloudStorageUrl || asset.transparentUrl}
                      download={`giffarine-${asset.productCode || 'product'}.png`}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full py-1.5 px-2.5 rounded-xl text-[11px] font-bold bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 flex items-center justify-center gap-1.5 transition-all"
                    >
                      <Download className="w-3 h-3" />
                      <span>ดาวน์โหลดไฟล์ PNG</span>
                    </a>

                    {/* Action 4: Permanent Delete */}
                    <button
                      onClick={() => handleDeleteAsset(asset)}
                      disabled={deletingId === asset.id}
                      className="w-full py-1.5 px-2.5 rounded-xl text-[11px] font-bold bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 flex items-center justify-center gap-1.5 transition-all active:scale-98 disabled:opacity-50"
                    >
                      <Trash2 className={`w-3 h-3 ${deletingId === asset.id ? 'animate-spin' : ''}`} />
                      <span>{deletingId === asset.id ? 'กำลังลบ...' : 'ลบภาพถาวร'}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
